import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Calendar, Clock, Edit2, Trash2, Filter, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, parseISO } from "date-fns";
import type { AssistantTimeEntry, Task, Goal } from "@shared/schema";
import { Link } from "wouter";
import { TimeClockWidget } from "@/components/time-clock-widget";

interface TimeEntryWithDetails extends AssistantTimeEntry {
  task?: { id: string; title: string } | null;
  goal?: { id: string; title: string } | null;
}

type DateFilter = "this-week" | "last-week" | "this-month" | "custom";

export default function TimeEntriesPage() {
  const { toast } = useToast();
  const [dateFilter, setDateFilter] = useState<DateFilter>("this-week");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [editingEntry, setEditingEntry] = useState<TimeEntryWithDetails | null>(null);
  const [editDescription, setEditDescription] = useState("");

  const getDateRange = () => {
    const now = new Date();
    switch (dateFilter) {
      case "this-week":
        return {
          startDate: format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"),
          endDate: format(endOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"),
        };
      case "last-week":
        const lastWeek = subWeeks(now, 1);
        return {
          startDate: format(startOfWeek(lastWeek, { weekStartsOn: 1 }), "yyyy-MM-dd"),
          endDate: format(endOfWeek(lastWeek, { weekStartsOn: 1 }), "yyyy-MM-dd"),
        };
      case "this-month":
        return {
          startDate: format(startOfMonth(now), "yyyy-MM-dd"),
          endDate: format(endOfMonth(now), "yyyy-MM-dd"),
        };
      case "custom":
        return {
          startDate: customStartDate || undefined,
          endDate: customEndDate || undefined,
        };
      default:
        return {};
    }
  };

  const dateRange = getDateRange();
  const queryParams = new URLSearchParams();
  if (dateRange.startDate) queryParams.set("startDate", dateRange.startDate);
  if (dateRange.endDate) queryParams.set("endDate", dateRange.endDate);
  const queryString = queryParams.toString();

  const { data: entries = [], isLoading } = useQuery<TimeEntryWithDetails[]>({
    queryKey: ["/api/time-entries", queryString],
    queryFn: async () => {
      const url = queryString ? `/api/time-entries?${queryString}` : "/api/time-entries";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch time entries");
      return res.json();
    },
  });

  const { data: assistantProfile } = useQuery({
    queryKey: ["/api/assistants/profile"],
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({ id, description }: { id: string; description: string }) =>
      apiRequest("PATCH", `/api/time-entries/${id}`, { description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      setEditingEntry(null);
      setEditDescription("");
      toast({
        title: "Entry Updated",
        description: "Time entry has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error?.message || "Failed to update entry.",
        variant: "destructive",
      });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/time-entries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      toast({
        title: "Entry Deleted",
        description: "Time entry has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: error?.message || "Failed to delete entry.",
        variant: "destructive",
      });
    },
  });

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return "—";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const totalMinutes = entries.reduce((sum, entry) => sum + (entry.durationMinutes || 0), 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  const handleEditEntry = (entry: TimeEntryWithDetails) => {
    setEditingEntry(entry);
    setEditDescription(entry.description || "");
  };

  const handleSaveEdit = () => {
    if (editingEntry) {
      updateEntryMutation.mutate({
        id: editingEntry.id,
        description: editDescription,
      });
    }
  };

  if (!assistantProfile) {
    return (
      <div className="p-4 md:p-6 w-full max-w-full">
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium">Time Tracking</h3>
            <p className="text-muted-foreground mt-2">
              Time tracking is only available for assistants.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 w-full max-w-full space-y-6">
      <div className="flex items-center gap-4">
        <Link href="/">
          <Button variant="ghost" size="icon" data-testid="button-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold" data-testid="page-title">Time Entries</h1>
          <p className="text-muted-foreground">
            Track and manage your work hours
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Time Clock
              </CardTitle>
              <CardDescription>Clock in and out to track your time</CardDescription>
            </CardHeader>
            <CardContent>
              <TimeClockWidget maxRecentEntries={3} />
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Time Entries</CardTitle>
                  <CardDescription>
                    Total: {totalHours > 0 ? `${totalHours}h ` : ""}{remainingMinutes}m
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilter)}>
                    <SelectTrigger className="w-[160px]" data-testid="select-date-filter">
                      <SelectValue placeholder="Filter by date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="this-week">This Week</SelectItem>
                      <SelectItem value="last-week">Last Week</SelectItem>
                      <SelectItem value="this-month">This Month</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {dateFilter === "custom" && (
                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                  <div className="flex-1">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      data-testid="input-start-date"
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      data-testid="input-end-date"
                    />
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12" data-testid="no-entries">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No time entries found for this period.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Clock In</TableHead>
                        <TableHead>Clock Out</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Task/Goal</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              {format(new Date(entry.clockInAt), "MMM d, yyyy")}
                            </div>
                          </TableCell>
                          <TableCell>{format(new Date(entry.clockInAt), "h:mm a")}</TableCell>
                          <TableCell>
                            {entry.clockOutAt ? (
                              format(new Date(entry.clockOutAt), "h:mm a")
                            ) : (
                              <Badge variant="outline" className="text-green-600">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {entry.clockOutAt ? formatDuration(entry.durationMinutes) : "In Progress"}
                          </TableCell>
                          <TableCell>
                            {entry.task?.title && (
                              <Badge variant="secondary" className="mr-1">
                                {entry.task.title.length > 20 ? entry.task.title.substring(0, 20) + "..." : entry.task.title}
                              </Badge>
                            )}
                            {entry.goal?.title && (
                              <Badge variant="outline">
                                {entry.goal.title.length > 20 ? entry.goal.title.substring(0, 20) + "..." : entry.goal.title}
                              </Badge>
                            )}
                            {!entry.task && !entry.goal && <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {entry.description || <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditEntry(entry)}
                                data-testid={`button-edit-${entry.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`button-delete-${entry.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Time Entry</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this time entry? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteEntryMutation.mutate(entry.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                      data-testid="button-confirm-delete"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
        <DialogContent data-testid="dialog-edit-entry">
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
            <DialogDescription>
              Update the description for this time entry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="What did you work on?"
                rows={3}
                data-testid="input-edit-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingEntry(null)}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateEntryMutation.isPending}
              data-testid="button-save-edit"
            >
              {updateEntryMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
